import { ReactNode, useEffect, useState } from 'react'
import styles from '../../assets/scss/components/collections/filter-accordions.module.scss'
import { motion, useAnimationControls } from 'framer-motion'
import { useI18n } from '../i18n.client'
import { EnhancedMenuItem } from '../../lib/menus'

const sortOptions = [
  {
    value: 'sortKey=CREATED&reverse=true',
    label: 'latest-arrivals',
  },
  {
    value: 'sortKey=PRICE&reverse=false',
    label: 'price-low-to-high',
  },
  {
    value: 'sortKey=PRICE&reverse=true',
    label: 'price-high-to-low',
  },
]

export const FilterAccordions: React.FC<{
  filterTagsColour: string[]
  filterTagsMaterial: string[]
  collectionsMenu?: EnhancedMenuItem
  showProductTypeFilter?: boolean
}> = ({ collectionsMenu, filterTagsColour, filterTagsMaterial, showProductTypeFilter }) => {
  const [i18n] = useI18n()
  const [productTypes, setProductTypes] = useState<string[]>()
  const [selectedTagsColour, setSelectedTagsColour] = useState<string[]>()
  const [selectedTagsMaterial, setSelectedTagsMaterial] = useState<string[]>()
  const [sortBy, setSortBy] = useState<string>()

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search)
    setProductTypes(
      searchParams.get('productTypes')
        ? searchParams.get('productTypes')?.split(',')
        : undefined
    )

    const tags = searchParams.get('tags') ? searchParams.get('tags')?.split(',') : undefined
    if (tags) {
      setSelectedTagsColour(tags.filter((tag) => filterTagsColour.includes(tag)))
      setSelectedTagsMaterial(tags.filter((tag) => filterTagsMaterial.includes(tag)))
    }

    const sortKey = searchParams.get('sortKey') || 'CREATED'
    const reverse = searchParams.get('reverse') || 'true'
    setSortBy(`sortKey=${sortKey}&reverse=${reverse}`)
  }, [])

  if (!collectionsMenu) {
    return null
  }

  return (
    <div className={styles.accordion}>
      <AccordionSegment title={i18n.translate('sort-by')}>
        {sortOptions.map((option) => (
          <label key={option.value} className={styles.filterOption}>
            <input
              name="sortBy"
              checked={option.value === sortBy}
              onChange={() => setSortBy(option.value)}
              type="radio"
              value={option.value}
            />
            <span className={styles.label}>{i18n.translate(option.label)}</span>
          </label>
        ))}
      </AccordionSegment>
      <AccordionSegment title={i18n.translate('colour')} count={selectedTagsColour?.length}>
        {filterTagsColour.map((tag) => (
          <label key={tag} className={styles.filterOption}>
            <input defaultChecked={selectedTagsColour?.includes(tag)} name="tags" type="checkbox" value={tag} />
            <span className={styles.label}>{i18n.translate(tag.toLowerCase().replaceAll(' ', '-'))}</span>
          </label>
        ))}
      </AccordionSegment>
      <AccordionSegment title={i18n.translate('material')} count={selectedTagsMaterial?.length}>
        {filterTagsMaterial.map((tag) => (
          <label key={tag} className={styles.filterOption}>
            <input defaultChecked={selectedTagsMaterial?.includes(tag)} name="tags" type="checkbox" value={tag} />
            <span className={styles.label}>{i18n.translate(tag.toLowerCase().replaceAll(' ', '-'))}</span>
          </label>
        ))}
      </AccordionSegment>
      {showProductTypeFilter && (
        <AccordionSegment title={i18n.translate('product-type')} count={productTypes?.length}>
          {collectionsMenu.items
            .filter((item) => item.to !== '/collections/all')
            .map((item) => (
              <label key={item.id} className={styles.filterOption}>
                <input
                  defaultChecked={productTypes?.includes(item.title)}
                  name="productTypes"
                  type="checkbox"
                  value={item.title}
                />
                <span className={styles.label}>{item.title}</span>
              </label>
            ))}
        </AccordionSegment>
      )}
    </div>
  )
}

const AccordionSegment: React.FC<{
  children: ReactNode
  count?: number
  title: string
}> = ({ children, count, title }) => {
  const [active, setActive] = useState(false)

  const variants = {
    exit: { opacity: 0, height: 0 },
    initial: { opacity: 0, height: 0 },
    visible: { opacity: 1, height: 'auto' },
  }
  const controls = useAnimationControls()

  const handleClick = () => {
    if (active) {
      setActive(false)
      controls.start('initial')
    } else {
      setActive(true)
      controls.start('visible')
    }
  }

  return (
    <div className={styles.segment}>
      <div className={styles.title} onClick={handleClick}>
        {title}
        {!!count && ` (${count})`}
        <div className={styles.icon}>{active ? '–' : '+'}</div>
      </div>
      <motion.div
        animate={controls}
        className={styles.expandCollapse}
        exit="exit"
        initial="initial"
        transition={{ ease: [0, 0, 0, 1] }}
        variants={variants}>
        <div className={styles.content}>{children}</div>
      </motion.div>
    </div>
  )
}
